import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  serverTimestamp
} from 'firebase/firestore'
import { db } from '../firebase/config'

// Standard Admin Credentials
export const ADMIN_EMAIL = 'admin@vtic.com'
export const ADMIN_PASSWORD = 'admin123'

// ==========================================
// LOCAL STORAGE MOCK DATABASE IMPLEMENTATION
// ==========================================

const getLocal = (key, defaultVal = []) => {
  try {
    const val = localStorage.getItem(key)
    return val ? JSON.parse(val) : defaultVal
  } catch {
    return defaultVal
  }
}

const setLocal = (key, val) => {
  try {
    localStorage.setItem(key, JSON.stringify(val))
  } catch {
    // Ignore storage quota or disabled errors
  }
}

// Check if Local Fallback Mode is active
export const isUsingLocalFallback = () => {
  return localStorage.getItem('vtic_library_use_local') === 'true'
}

// Enable local fallback mode when Firestore permissions fail
const enableLocalMode = () => {
  if (localStorage.getItem('vtic_library_use_local') !== 'true') {
    console.warn("Firestore rules blocked access. VTIC Library is falling back to LocalStorage.")
    localStorage.setItem('vtic_library_use_local', 'true')
    // Dispatch event to let react component know to display local fallback banner
    window.dispatchEvent(new Event('vtic_library_mode_change'))
  }
}

// Detect firebase permission denied error
const isPermissionError = (error) => {
  const code = error?.code || ''
  const msg = error?.message || ''
  return code === 'permission-denied' || 
         msg.toLowerCase().includes('permission') || 
         msg.toLowerCase().includes('insufficient')
}

// Generate unique ID for mock elements
const generateMockId = (prefix) => {
  return `${prefix}_${Math.random().toString(36).substr(2, 9)}`
}

// Mock Timestamp generator
const makeMockTimestamp = () => {
  const d = new Date()
  return {
    seconds: Math.floor(d.getTime() / 1000),
    nanoseconds: 0,
    toDate: () => d,
    toISOString: () => d.toISOString()
  }
}

// ==========================================
// UNIFIED AUTHENTICATION & LOGIN
// ==========================================

export const loginLibraryUser = async (email, password) => {
  const trimmedEmail = email.trim().toLowerCase()
  
  // 1. Validar administrador principal
  if (trimmedEmail === ADMIN_EMAIL && password === ADMIN_PASSWORD) {
    return { 
      success: true, 
      user: { 
        id: 'admin_library_id',
        role: 'admin', 
        email: ADMIN_EMAIL, 
        fullName: 'Administrador de Biblioteca' 
      } 
    }
  }

  // Si ya estamos en modo local, hacer login local directamente
  if (isUsingLocalFallback()) {
    const localMembers = getLocal('vtic_local_library_members')
    const matched = localMembers.find(m => m.email === trimmedEmail && m.password === password)
    if (matched) {
      return { success: true, user: matched }
    }
    return { success: false, error: 'Credenciales incorrectas para la Biblioteca (Modo Local).' }
  }

  try {
    const membersRef = collection(db, 'library_members')
    const q = query(membersRef, where('email', '==', trimmedEmail), where('password', '==', password))
    const querySnapshot = await getDocs(q)
    
    if (!querySnapshot.empty) {
      const docSnap = querySnapshot.docs[0]
      return {
        success: true,
        user: {
          id: docSnap.id,
          role: 'student',
          ...docSnap.data()
        }
      }
    }
    
    return { success: false, error: 'Credenciales incorrectas para la Biblioteca.' }
  } catch (error) {
    if (isPermissionError(error)) {
      enableLocalMode()
      // Ejecutar autenticación en base local
      const localMembers = getLocal('vtic_local_library_members')
      const matched = localMembers.find(m => m.email === trimmedEmail && m.password === password)
      if (matched) {
        return { success: true, user: matched }
      }
      return { success: false, error: 'Credenciales incorrectas para la Biblioteca (Modo Local).' }
    }
    return { success: false, error: 'Error en inicio de sesión: ' + error.message }
  }
}

// ==========================================
// STUDENT MEMBERS MANAGEMENT (ADMIN ONLY)
// ==========================================

export const createLibraryMember = async (fullName, email, password) => {
  const trimmedEmail = email.trim().toLowerCase()
  
  if (trimmedEmail === ADMIN_EMAIL) {
    return { success: false, error: 'No se puede registrar una cuenta con el correo de administrador.' }
  }

  if (isUsingLocalFallback()) {
    const localMembers = getLocal('vtic_local_library_members')
    if (localMembers.some(m => m.email === trimmedEmail)) {
      return { success: false, error: 'Ya existe un estudiante registrado con este correo (Modo Local).' }
    }
    const newStudent = {
      id: generateMockId('member'),
      fullName: fullName.trim(),
      email: trimmedEmail,
      password: password,
      role: 'student',
      createdAt: makeMockTimestamp()
    }
    localMembers.push(newStudent)
    setLocal('vtic_local_library_members', localMembers)
    return { success: true, data: newStudent }
  }

  try {
    const membersRef = collection(db, 'library_members')
    const q = query(membersRef, where('email', '==', trimmedEmail))
    const existing = await getDocs(q)
    if (!existing.empty) {
      return { success: false, error: 'Ya existe un estudiante registrado con este correo.' }
    }
    
    const docRef = await addDoc(membersRef, {
      fullName: fullName.trim(),
      email: trimmedEmail,
      password: password,
      role: 'student',
      createdAt: serverTimestamp()
    })
    
    return { 
      success: true, 
      data: { id: docRef.id, fullName, email: trimmedEmail, role: 'student' } 
    }
  } catch (error) {
    if (isPermissionError(error)) {
      enableLocalMode()
      // Re-ejecutar en modo local
      const localMembers = getLocal('vtic_local_library_members')
      if (localMembers.some(m => m.email === trimmedEmail)) {
        return { success: false, error: 'Ya existe un estudiante registrado con este correo (Modo Local).' }
      }
      const newStudent = {
        id: generateMockId('member'),
        fullName: fullName.trim(),
        email: trimmedEmail,
        password: password,
        role: 'student',
        createdAt: makeMockTimestamp()
      }
      localMembers.push(newStudent)
      setLocal('vtic_local_library_members', localMembers)
      return { success: true, data: newStudent }
    }
    return { success: false, error: 'Error al registrar estudiante: ' + error.message }
  }
}

export const getLibraryMembers = async () => {
  if (isUsingLocalFallback()) {
    const localMembers = getLocal('vtic_local_library_members')
    // Ordenar por fecha de creación descendente
    localMembers.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))
    return { success: true, data: localMembers }
  }

  try {
    const membersRef = collection(db, 'library_members')
    const querySnapshot = await getDocs(membersRef)
    
    const members = []
    querySnapshot.forEach((doc) => {
      members.push({ id: doc.id, ...doc.data() })
    })
    
    members.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))
    return { success: true, data: members }
  } catch (error) {
    if (isPermissionError(error)) {
      enableLocalMode()
      const localMembers = getLocal('vtic_local_library_members')
      localMembers.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))
      return { success: true, data: localMembers }
    }
    return { success: false, error: 'Error al obtener estudiantes: ' + error.message }
  }
}

// ==========================================
// SECTIONS MANAGEMENT
// ==========================================

export const getLibrarySections = async () => {
  if (isUsingLocalFallback()) {
    const localSections = getLocal('vtic_local_library_sections')
    localSections.sort((a, b) => a.name.localeCompare(b.name))
    return { success: true, data: localSections }
  }

  try {
    const sectionsRef = collection(db, 'library_sections')
    const querySnapshot = await getDocs(sectionsRef)
    
    if (querySnapshot.empty) {
      return { success: true, data: [] }
    }
    
    const sections = []
    querySnapshot.forEach((doc) => {
      sections.push({ id: doc.id, ...doc.data() })
    })
    
    sections.sort((a, b) => a.name.localeCompare(b.name))
    return { success: true, data: sections }
  } catch (error) {
    if (isPermissionError(error)) {
      enableLocalMode()
      const localSections = getLocal('vtic_local_library_sections')
      localSections.sort((a, b) => a.name.localeCompare(b.name))
      return { success: true, data: localSections }
    }
    return { success: false, error: error.message }
  }
}

export const createLibrarySection = async (name, description) => {
  if (isUsingLocalFallback()) {
    const localSections = getLocal('vtic_local_library_sections')
    if (localSections.some(s => s.name.toLowerCase() === name.toLowerCase())) {
      return { success: false, error: 'Ya existe una sección con este nombre (Modo Local).' }
    }
    const newSec = {
      id: generateMockId('section'),
      name,
      description,
      createdAt: makeMockTimestamp()
    }
    localSections.push(newSec)
    setLocal('vtic_local_library_sections', localSections)
    return { success: true, data: newSec }
  }

  try {
    const sectionsRef = collection(db, 'library_sections')
    const q = query(sectionsRef, where('name', '==', name))
    const existing = await getDocs(q)
    if (!existing.empty) {
      return { success: false, error: 'Ya existe una sección con este nombre.' }
    }
    
    const docRef = await addDoc(sectionsRef, {
      name,
      description,
      createdAt: serverTimestamp()
    })
    
    return { success: true, data: { id: docRef.id, name, description } }
  } catch (error) {
    if (isPermissionError(error)) {
      enableLocalMode()
      const localSections = getLocal('vtic_local_library_sections')
      if (localSections.some(s => s.name.toLowerCase() === name.toLowerCase())) {
        return { success: false, error: 'Ya existe una sección con este nombre (Modo Local).' }
      }
      const newSec = {
        id: generateMockId('section'),
        name,
        description,
        createdAt: makeMockTimestamp()
      }
      localSections.push(newSec)
      setLocal('vtic_local_library_sections', localSections)
      return { success: true, data: newSec }
    }
    return { success: false, error: error.message }
  }
}

// ==========================================
// BOOKS MANAGEMENT
// ==========================================

export const getLibraryBooks = async () => {
  if (isUsingLocalFallback()) {
    const localBooks = getLocal('vtic_local_library_books')
    localBooks.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))
    return { success: true, data: localBooks }
  }

  try {
    const booksRef = collection(db, 'library_books')
    const querySnapshot = await getDocs(booksRef)
    
    if (querySnapshot.empty) {
      return { success: true, data: [] }
    }
    
    const books = []
    querySnapshot.forEach((doc) => {
      books.push({ id: doc.id, ...doc.data() })
    })
    
    books.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))
    return { success: true, data: books }
  } catch (error) {
    if (isPermissionError(error)) {
      enableLocalMode()
      const localBooks = getLocal('vtic_local_library_books')
      localBooks.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))
      return { success: true, data: localBooks }
    }
    return { success: false, error: error.message }
  }
}

export const addLibraryBook = async (bookData) => {
  if (isUsingLocalFallback()) {
    const localBooks = getLocal('vtic_local_library_books')
    const newBook = {
      id: generateMockId('book'),
      ...bookData,
      status: 'available',
      reservedBy: null,
      reservedByName: null,
      reservedAt: null,
      createdAt: makeMockTimestamp()
    }
    localBooks.push(newBook)
    setLocal('vtic_local_library_books', localBooks)
    return { success: true, data: newBook }
  }

  try {
    const booksRef = collection(db, 'library_books')
    const docRef = await addDoc(booksRef, {
      ...bookData,
      status: 'available',
      reservedBy: null,
      reservedByName: null,
      reservedAt: null,
      createdAt: serverTimestamp()
    })
    
    return { success: true, data: { id: docRef.id, ...bookData, status: 'available' } }
  } catch (error) {
    if (isPermissionError(error)) {
      enableLocalMode()
      const localBooks = getLocal('vtic_local_library_books')
      const newBook = {
        id: generateMockId('book'),
        ...bookData,
        status: 'available',
        reservedBy: null,
        reservedByName: null,
        reservedAt: null,
        createdAt: makeMockTimestamp()
      }
      localBooks.push(newBook)
      setLocal('vtic_local_library_books', localBooks)
      return { success: true, data: newBook }
    }
    return { success: false, error: error.message }
  }
}

export const deleteLibraryBook = async (bookId) => {
  if (isUsingLocalFallback()) {
    // 1. Borrar libro de la base local
    let localBooks = getLocal('vtic_local_library_books')
    localBooks = localBooks.filter(b => b.id !== bookId)
    setLocal('vtic_local_library_books', localBooks)

    // 2. Cancelar reservas activas de ese libro en local
    let localReservations = getLocal('vtic_local_library_reservations')
    localReservations = localReservations.map(res => {
      if (res.bookId === bookId && (res.status === 'active' || res.status === 'pending_approval')) {
        return {
          ...res,
          status: 'cancelled',
          cancelledAt: makeMockTimestamp()
        }
      }
      return res
    })
    setLocal('vtic_local_library_reservations', localReservations)
    return { success: true }
  }

  try {
    // 1. Borrar libro
    await deleteDoc(doc(db, 'library_books', bookId))
    
    // 2. Opcionalmente cancelar cualquier reserva activa de este libro
    const reservationsRef = collection(db, 'library_reservations')
    const q = query(reservationsRef, where('bookId', '==', bookId))
    const activeReservations = await getDocs(q)
    
    const cancelPromises = activeReservations.docs.map(async (resDoc) => {
      if (resDoc.data().status === 'active' || resDoc.data().status === 'pending_approval') {
        await updateDoc(doc(db, 'library_reservations', resDoc.id), {
          status: 'cancelled',
          cancelledAt: serverTimestamp()
        })
      }
    })
    await Promise.all(cancelPromises)
    
    return { success: true }
  } catch (error) {
    if (isPermissionError(error)) {
      enableLocalMode()
      // Eliminar en base local
      let localBooks = getLocal('vtic_local_library_books')
      localBooks = localBooks.filter(b => b.id !== bookId)
      setLocal('vtic_local_library_books', localBooks)

      let localReservations = getLocal('vtic_local_library_reservations')
      localReservations = localReservations.map(res => {
        if (res.bookId === bookId && (res.status === 'active' || res.status === 'pending_approval')) {
          return {
            ...res,
            status: 'cancelled',
            cancelledAt: makeMockTimestamp()
          }
        }
        return res
      })
      setLocal('vtic_local_library_reservations', localReservations)
      return { success: true }
    }
    return { success: false, error: error.message }
  }
}

// ==========================================
// RESERVATIONS & LOANS FLOW
// ==========================================

export const reserveLibraryBook = async (bookId, bookTitle, bookAuthor, userId, userName, userEmail) => {
  if (isUsingLocalFallback()) {
    const localBooks = getLocal('vtic_local_library_books')
    const bookIdx = localBooks.findIndex(b => b.id === bookId)
    
    if (bookIdx === -1) {
      return { success: false, error: 'El libro no existe en la biblioteca local.' }
    }
    
    if (localBooks[bookIdx].status !== 'available') {
      return { success: false, error: 'El libro ya está reservado o pendiente de aprobación.' }
    }
    
    // 1. Crear reserva
    const reservedAt = new Date().toISOString()
    const localReservations = getLocal('vtic_local_library_reservations')
    const newReservation = {
      id: generateMockId('reservation'),
      bookId,
      bookTitle,
      bookAuthor,
      userId,
      userName,
      userEmail,
      status: 'pending_approval',
      reservedAt,
      dueDate: null,
      returnedAt: null,
      approvedAt: null,
      rejectedAt: null
    }
    localReservations.push(newReservation)
    setLocal('vtic_local_library_reservations', localReservations)
    
    // 2. Modificar estado del libro
    localBooks[bookIdx].status = 'pending'
    localBooks[bookIdx].reservedBy = userId
    localBooks[bookIdx].reservedByName = userName
    localBooks[bookIdx].reservedAt = makeMockTimestamp()
    setLocal('vtic_local_library_books', localBooks)
    
    return { success: true, reservationId: newReservation.id }
  }

  try {
    const bookRef = doc(db, 'library_books', bookId)
    const bookSnap = await getDoc(bookRef)
    
    if (!bookSnap.exists()) {
      return { success: false, error: 'El libro no existe.' }
    }
    
    const bookData = bookSnap.data()
    if (bookData.status !== 'available') {
      return { success: false, error: 'El libro ya está reservado o pendiente de aprobación.' }
    }
    
    const reservedAt = new Date()
    const reservationsRef = collection(db, 'library_reservations')
    const reservationDoc = await addDoc(reservationsRef, {
      bookId,
      bookTitle,
      bookAuthor,
      userId,
      userName,
      userEmail,
      status: 'pending_approval',
      reservedAt,
      dueDate: null,
      returnedAt: null,
      approvedAt: null,
      rejectedAt: null
    })
    
    await updateDoc(bookRef, {
      status: 'pending',
      reservedBy: userId,
      reservedByName: userName,
      reservedAt: serverTimestamp()
    })
    
    return { 
      success: true, 
      reservationId: reservationDoc.id
    }
  } catch (error) {
    if (isPermissionError(error)) {
      enableLocalMode()
      // Ejecutar localmente
      const localBooks = getLocal('vtic_local_library_books')
      const bookIdx = localBooks.findIndex(b => b.id === bookId)
      if (bookIdx === -1) return { success: false, error: 'El libro no existe.' }
      if (localBooks[bookIdx].status !== 'available') {
        return { success: false, error: 'El libro ya está reservado o pendiente de aprobación.' }
      }
      
      const reservedAt = new Date().toISOString()
      const localReservations = getLocal('vtic_local_library_reservations')
      const newReservation = {
        id: generateMockId('reservation'),
        bookId,
        bookTitle,
        bookAuthor,
        userId,
        userName,
        userEmail,
        status: 'pending_approval',
        reservedAt,
        dueDate: null,
        returnedAt: null,
        approvedAt: null,
        rejectedAt: null
      }
      localReservations.push(newReservation)
      setLocal('vtic_local_library_reservations', localReservations)
      
      localBooks[bookIdx].status = 'pending'
      localBooks[bookIdx].reservedBy = userId
      localBooks[bookIdx].reservedByName = userName
      localBooks[bookIdx].reservedAt = makeMockTimestamp()
      setLocal('vtic_local_library_books', localBooks)
      
      return { success: true, reservationId: newReservation.id }
    }
    return { success: false, error: error.message }
  }
}

export const approveLibraryReservation = async (reservationId, bookId) => {
  if (isUsingLocalFallback()) {
    const approvedAt = new Date()
    const dueDate = new Date()
    dueDate.setDate(approvedAt.getDate() + 7)

    // 1. Actualizar reserva en local
    const localReservations = getLocal('vtic_local_library_reservations')
    const resIdx = localReservations.findIndex(r => r.id === reservationId)
    if (resIdx !== -1) {
      localReservations[resIdx].status = 'active'
      localReservations[resIdx].approvedAt = makeMockTimestamp()
      localReservations[resIdx].dueDate = dueDate.toISOString()
      setLocal('vtic_local_library_reservations', localReservations)
    }

    // 2. Actualizar libro en local
    const localBooks = getLocal('vtic_local_library_books')
    const bookIdx = localBooks.findIndex(b => b.id === bookId)
    if (bookIdx !== -1) {
      localBooks[bookIdx].status = 'reserved'
      setLocal('vtic_local_library_books', localBooks)
    }

    return { success: true, dueDate }
  }

  try {
    const approvedAt = new Date()
    const dueDate = new Date()
    dueDate.setDate(approvedAt.getDate() + 7)
    
    const reservationRef = doc(db, 'library_reservations', reservationId)
    await updateDoc(reservationRef, {
      status: 'active',
      approvedAt: serverTimestamp(),
      dueDate: dueDate
    })
    
    const bookRef = doc(db, 'library_books', bookId)
    await updateDoc(bookRef, {
      status: 'reserved'
    })
    
    return { success: true, dueDate }
  } catch (error) {
    if (isPermissionError(error)) {
      enableLocalMode()
      const approvedAt = new Date()
      const dueDate = new Date()
      dueDate.setDate(approvedAt.getDate() + 7)

      const localReservations = getLocal('vtic_local_library_reservations')
      const resIdx = localReservations.findIndex(r => r.id === reservationId)
      if (resIdx !== -1) {
        localReservations[resIdx].status = 'active'
        localReservations[resIdx].approvedAt = makeMockTimestamp()
        localReservations[resIdx].dueDate = dueDate.toISOString()
        setLocal('vtic_local_library_reservations', localReservations)
      }

      const localBooks = getLocal('vtic_local_library_books')
      const bookIdx = localBooks.findIndex(b => b.id === bookId)
      if (bookIdx !== -1) {
        localBooks[bookIdx].status = 'reserved'
        setLocal('vtic_local_library_books', localBooks)
      }

      return { success: true, dueDate }
    }
    return { success: false, error: error.message }
  }
}

export const rejectLibraryReservation = async (reservationId, bookId) => {
  if (isUsingLocalFallback()) {
    // 1. Actualizar reserva local
    const localReservations = getLocal('vtic_local_library_reservations')
    const resIdx = localReservations.findIndex(r => r.id === reservationId)
    if (resIdx !== -1) {
      localReservations[resIdx].status = 'rejected'
      localReservations[resIdx].rejectedAt = makeMockTimestamp()
      setLocal('vtic_local_library_reservations', localReservations)
    }

    // 2. Liberar libro local
    const localBooks = getLocal('vtic_local_library_books')
    const bookIdx = localBooks.findIndex(b => b.id === bookId)
    if (bookIdx !== -1) {
      localBooks[bookIdx].status = 'available'
      localBooks[bookIdx].reservedBy = null
      localBooks[bookIdx].reservedByName = null
      localBooks[bookIdx].reservedAt = null
      setLocal('vtic_local_library_books', localBooks)
    }

    return { success: true }
  }

  try {
    const reservationRef = doc(db, 'library_reservations', reservationId)
    await updateDoc(reservationRef, {
      status: 'rejected',
      rejectedAt: serverTimestamp()
    })
    
    const bookRef = doc(db, 'library_books', bookId)
    await updateDoc(bookRef, {
      status: 'available',
      reservedBy: null,
      reservedByName: null,
      reservedAt: null
    })
    
    return { success: true }
  } catch (error) {
    if (isPermissionError(error)) {
      enableLocalMode()
      const localReservations = getLocal('vtic_local_library_reservations')
      const resIdx = localReservations.findIndex(r => r.id === reservationId)
      if (resIdx !== -1) {
        localReservations[resIdx].status = 'rejected'
        localReservations[resIdx].rejectedAt = makeMockTimestamp()
        setLocal('vtic_local_library_reservations', localReservations)
      }

      const localBooks = getLocal('vtic_local_library_books')
      const bookIdx = localBooks.findIndex(b => b.id === bookId)
      if (bookIdx !== -1) {
        localBooks[bookIdx].status = 'available'
        localBooks[bookIdx].reservedBy = null
        localBooks[bookIdx].reservedByName = null
        localBooks[bookIdx].reservedAt = null
        setLocal('vtic_local_library_books', localBooks)
      }

      return { success: true }
    }
    return { success: false, error: error.message }
  }
}

export const returnLibraryBook = async (reservationId, bookId) => {
  if (isUsingLocalFallback()) {
    // 1. Actualizar reserva local
    const localReservations = getLocal('vtic_local_library_reservations')
    const resIdx = localReservations.findIndex(r => r.id === reservationId)
    if (resIdx !== -1) {
      localReservations[resIdx].status = 'returned'
      localReservations[resIdx].returnedAt = makeMockTimestamp()
      setLocal('vtic_local_library_reservations', localReservations)
    }

    // 2. Liberar libro local
    const localBooks = getLocal('vtic_local_library_books')
    const bookIdx = localBooks.findIndex(b => b.id === bookId)
    if (bookIdx !== -1) {
      localBooks[bookIdx].status = 'available'
      localBooks[bookIdx].reservedBy = null
      localBooks[bookIdx].reservedByName = null
      localBooks[bookIdx].reservedAt = null
      setLocal('vtic_local_library_books', localBooks)
    }

    return { success: true }
  }

  try {
    const reservationRef = doc(db, 'library_reservations', reservationId)
    await updateDoc(reservationRef, {
      status: 'returned',
      returnedAt: serverTimestamp()
    })
    
    const bookRef = doc(db, 'library_books', bookId)
    await updateDoc(bookRef, {
      status: 'available',
      reservedBy: null,
      reservedByName: null,
      reservedAt: null
    })
    
    return { success: true }
  } catch (error) {
    if (isPermissionError(error)) {
      enableLocalMode()
      const localReservations = getLocal('vtic_local_library_reservations')
      const resIdx = localReservations.findIndex(r => r.id === reservationId)
      if (resIdx !== -1) {
        localReservations[resIdx].status = 'returned'
        localReservations[resIdx].returnedAt = makeMockTimestamp()
        setLocal('vtic_local_library_reservations', localReservations)
      }

      const localBooks = getLocal('vtic_local_library_books')
      const bookIdx = localBooks.findIndex(b => b.id === bookId)
      if (bookIdx !== -1) {
        localBooks[bookIdx].status = 'available'
        localBooks[bookIdx].reservedBy = null
        localBooks[bookIdx].reservedByName = null
        localBooks[bookIdx].reservedAt = null
        setLocal('vtic_local_library_books', localBooks)
      }

      return { success: true }
    }
    return { success: false, error: error.message }
  }
}

export const getUserReservations = async (userId) => {
  if (isUsingLocalFallback()) {
    const localReservations = getLocal('vtic_local_library_reservations')
    const filtered = localReservations.filter(r => r.userId === userId)
    filtered.sort((a, b) => {
      const dateA = a.reservedAt?.seconds ? a.reservedAt.seconds : new Date(a.reservedAt).getTime() / 1000
      const dateB = b.reservedAt?.seconds ? b.reservedAt.seconds : new Date(b.reservedAt).getTime() / 1000
      return dateB - dateA
    })
    return { success: true, data: filtered }
  }

  try {
    const reservationsRef = collection(db, 'library_reservations')
    const q = query(reservationsRef, where('userId', '==', userId))
    const querySnapshot = await getDocs(q)
    
    const reservations = []
    querySnapshot.forEach((doc) => {
      reservations.push({ id: doc.id, ...doc.data() })
    })
    
    reservations.sort((a, b) => {
      const dateA = a.reservedAt?.seconds ? a.reservedAt.seconds : new Date(a.reservedAt).getTime() / 1000
      const dateB = b.reservedAt?.seconds ? b.reservedAt.seconds : new Date(b.reservedAt).getTime() / 1000
      return dateB - dateA
    })
    
    return { success: true, data: reservations }
  } catch (error) {
    if (isPermissionError(error)) {
      enableLocalMode()
      const localReservations = getLocal('vtic_local_library_reservations')
      const filtered = localReservations.filter(r => r.userId === userId)
      filtered.sort((a, b) => {
        const dateA = a.reservedAt?.seconds ? a.reservedAt.seconds : new Date(a.reservedAt).getTime() / 1000
        const dateB = b.reservedAt?.seconds ? b.reservedAt.seconds : new Date(b.reservedAt).getTime() / 1000
        return dateB - dateA
      })
      return { success: true, data: filtered }
    }
    return { success: false, error: error.message }
  }
}

export const getAllReservations = async () => {
  if (isUsingLocalFallback()) {
    const localReservations = getLocal('vtic_local_library_reservations')
    localReservations.sort((a, b) => {
      const dateA = a.reservedAt?.seconds ? a.reservedAt.seconds : new Date(a.reservedAt).getTime() / 1000
      const dateB = b.reservedAt?.seconds ? b.reservedAt.seconds : new Date(b.reservedAt).getTime() / 1000
      return dateB - dateA
    })
    return { success: true, data: localReservations }
  }

  try {
    const reservationsRef = collection(db, 'library_reservations')
    const querySnapshot = await getDocs(reservationsRef)
    
    const reservations = []
    querySnapshot.forEach((doc) => {
      reservations.push({ id: doc.id, ...doc.data() })
    })
    
    reservations.sort((a, b) => {
      const dateA = a.reservedAt?.seconds ? a.reservedAt.seconds : new Date(a.reservedAt).getTime() / 1000
      const dateB = b.reservedAt?.seconds ? b.reservedAt.seconds : new Date(b.reservedAt).getTime() / 1000
      return dateB - dateA
    })
    
    return { success: true, data: reservations }
  } catch (error) {
    if (isPermissionError(error)) {
      enableLocalMode()
      const localReservations = getLocal('vtic_local_library_reservations')
      localReservations.sort((a, b) => {
        const dateA = a.reservedAt?.seconds ? a.reservedAt.seconds : new Date(a.reservedAt).getTime() / 1000
        const dateB = b.reservedAt?.seconds ? b.reservedAt.seconds : new Date(b.reservedAt).getTime() / 1000
        return dateB - dateA
      })
      return { success: true, data: localReservations }
    }
    return { success: false, error: error.message }
  }
}
